import styled from 'styled-components'
import { FC, useEffect, useState } from 'react'
import { Box } from '@/components/atoms/Grid'
import { validFor } from '@/theme/validFor'
import { useScrollCondition } from 'hooks/useScroll'
import useTranslation from 'next-translate/useTranslation'
import { useActiveFiltersCount } from '@/providers/filterProvider/filtersProvider'
import { BigBadgeRedesign } from '@/components/atoms/badge'
import { FilterIconRedesignDefault } from '@/components/atoms/icons/FilterIconRedesignDefault'

type StickyFilterButtonProps = {
  onClick: () => void
  hidden?: boolean
  showUnder?: number
}

const StickyFilterButton: FC<StickyFilterButtonProps> = ({
  onClick,
  hidden = false,
  showUnder = 350
}) => {
  const visible = useScrollCondition((scroll) => scroll > showUnder)
  const count = useActiveFiltersCount()
  const { t } = useTranslation('common')
  const title = t('Category.Filter.title')
  const [headerBottom, setHeaderBottom] = useState(0)

  useEffect(() => {
    if (!visible) {
      return
    }
    const header = document.querySelector('header')
    const update = () =>
      setHeaderBottom(Math.max(header?.getBoundingClientRect().bottom ?? 0, 0))
    update()
    window.addEventListener('resize', update)
    const observer =
      header && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(update)
        : null
    observer?.observe(header)
    return () => {
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [visible])

  return (
    <StyledBox visible={visible && !hidden} offsetTop={headerBottom + 10}>
      <StyledButton
        onClick={onClick}
        aria-label={title}
        data-testid="stickyMobileFilter"
      >
        <FilterIconRedesignDefault />
        <StyledTitle>{title}</StyledTitle>
        {count > 0 && <StyledBadge counter={count} />}
      </StyledButton>
    </StyledBox>
  )
}

export default StickyFilterButton

const StyledBadge = styled(BigBadgeRedesign)`
  margin-left: 6px;
  min-width: 14px;
  height: 14px;
  border-radius: 14px;
  padding: 2px;

  p {
    font-size: 10px;
  }
`

const StyledTitle = styled.span`
  margin-left: 6px;
`

const StyledButton = styled.button`
  height: 30px;
  padding: 0 10px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 4px 4px 10px 0 rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;

  @supports not (
    (backdrop-filter: blur(12px)) or (-webkit-backdrop-filter: blur(12px))
  ) {
    background: #ffffff;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 4px 6px 12px 0 rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
`

const StyledBox = styled(Box)`
  position: fixed;
  right: 20px;
  top: ${({ offsetTop }) => offsetTop}px;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: visibility 0.2s, opacity 0.5s linear;
  z-index: 89;
  ${validFor.mobile`
    right: 12px;
  `}
`
